import { clsx } from 'clsx';
import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ESearchFilters } from '@/constants/searchFilters';
import { useAppLayout } from '@/context/AppLayoutContext';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import useQueryParams from '@/hooks/useQueryParams';
import { useRouter } from '@/hooks/useRouter';
import { useGetWishlists } from '@/hooks/useWishlists';
import { selectAuthenticated } from '@/redux/modules/auth/selectors';
import { setWishlistedItems } from '@/redux/modules/wishlists';
import { ERentalType } from '@/services/analytics/types';
import { ERentalCategory } from '@/services/types/search/rentals/id';
import { EWishlistRoles } from '@/utility/wishlists';

import { AiSearchProvider } from '../ai-search/AiSearchContext';
import AiSearchTopBar from '../ai-search/AiSearchTopBar';
import { Impersonated } from '../auth/Impersonated/Impersonated';
import GlobalHeader from '../ui/GlobalHeader/GlobalHeader';
import { resolvePageHeaderFacts } from '../ui/GlobalHeader/GlobalHeaderV2/headerConfig';
import Footer from './footer/Footer';
import { PromoBanner } from './PromoBanner';
import SystemDowntimeNotice from './SystemDowntimeNotice';

const LoginModalContainer = lazy(() => import('@/components/auth/LoginModalContainer'));
const SignupModalContainer = lazy(() => import('@/components/auth/SignupModalContainer'));
const PhoneVerificationGate = lazy(() => import('@/components/auth/PhoneVerificationGate'));

const ApplicationLayout = ({ children, pageProps, header }) => {
  const router = useRouter();
  const { isMobile } = useBreakpoint();

  // Every page's header chrome comes from its declared preset — the static
  // `Page.header = { preset }` forwarded from _app as Component.header.
  // Undeclared pages get the default: opaque, contained, sticky, no center
  // search. Runtime facts (breakpoint, rental-category query, signature
  // campgrounds, 2FA) stay computed below.
  const {
    headerIsTransparent,
    headerDesktopTransparent,
    isDarkNavigation,
    isHeaderWhite,
    isFluidPage,
    nonStickyHeader,
    hideHeaderAndFooter,
    mobileHideHeaderAndFooter,
    hideFooter,
    stickyFromLg,
    mobileAlwaysSticky,
    isSERP,
    noSearchNavbar,
    isRVListingPage,
    deferSearchNavigationUntilSubmit,
    isCampgroundSearchPage,
  } = resolvePageHeaderFacts(header ?? { preset: 'default' }, { isMobile });

  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectAuthenticated);

  const isNewLayout =
    pageProps.isHomePage || pageProps.isRvDeliveryPage || pageProps.isNewPromoPage;
  const queryParams = useQueryParams();

  // Signature campgrounds render the book-a-site flow full screen. Driven by a
  // query param, not page identity, so it cannot live in the preset.
  const isSignatureCampgroundBookASite =
    router.pathname.startsWith('/campground-rental') && queryParams.signature === 'true';

  const shouldHideHeaderAndFooter =
    isSignatureCampgroundBookASite ||
    hideHeaderAndFooter ||
    (isMobile && mobileHideHeaderAndFooter);

  const shouldHideFooter = shouldHideHeaderAndFooter || hideFooter;

  const rentalCategory = ERentalCategory.RV;

  const { data: wishlists } = useGetWishlists({
    isAuthenticated,
    examplesOnly: false,
    excludeExamples: true,
    rentalCategory,
  });

  useEffect(() => {
    // We use SWR to fetch wishlists, but in order to keep using the funtionality build around favorites,
    // we need to keep the redux store updated with the wishlisted rentals
    if (wishlists && isAuthenticated) {
      const wishlistedRentals = wishlists
        .flatMap(wishlist =>
          wishlist?.rentals?.map(rental_id => ({
            id: wishlist.id,
            item_id: rental_id,
            item_type: 'rental',
            wishlist_creator_id: wishlist?.user_details?.find(
              user => user.user_role === EWishlistRoles.Owner,
            )?.id,
            wishlist_name: wishlist.title,
            wishlist_id: wishlist.id,
          })),
        )
        .filter(Boolean);

      const wishlistedCampgrounds = wishlists
        .flatMap(wishlist =>
          wishlist?.campgrounds?.map(campground_id => ({
            id: wishlist.id,
            item_id: campground_id,
            item_type: 'campground',
            wishlist_creator_id: wishlist?.user_details?.find(
              user => user.user_role === EWishlistRoles.Owner,
            )?.id,
            wishlist_name: wishlist.title,
            wishlist_id: wishlist.id,
          })),
        )
        .filter(Boolean);
      dispatch(
        setWishlistedItems({ rentals: wishlistedRentals, campgrounds: wishlistedCampgrounds }),
      );
    } else {
      dispatch(setWishlistedItems({ rentals: [], campgrounds: [] }));
    }
  }, [wishlists, isAuthenticated, dispatch]);

  const isRv =
    !router.query[ESearchFilters.RENTAL_CATEGORY] ||
    router.query[ESearchFilters.RENTAL_CATEGORY] === ERentalType.RV;

  const alwaysStickyHeaders = isMobile && mobileAlwaysSticky && isRv;

  const hasStickyHeader = !nonStickyHeader && !shouldHideHeaderAndFooter;
  const hasStuckHeaderShadow = isNewLayout && hasStickyHeader;

  const stuckHeaderCheckRef = useRef(null);
  const [headerIsStuck, setHeaderIsStuck] = useState(false);

  const { setHeaderHeight } = useAppLayout();
  const headerRef = useRef(null);

  useEffect(() => {
    if (!hasStuckHeaderShadow || !stuckHeaderCheckRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => setHeaderIsStuck(!!entry && entry.intersectionRatio < 1),
      { rootMargin: '1px 0px 0px 0px', threshold: [1] },
    );

    observer.observe(stuckHeaderCheckRef.current);

    return () => observer.disconnect();
  }, [hasStuckHeaderShadow]);

  useEffect(() => {
    if (!headerRef.current) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setHeaderHeight(entry.contentRect.height);
      }
    });

    observer.observe(headerRef.current);

    return () => observer.disconnect();
  }, [setHeaderHeight]);

  return (
    <>
      <Suspense fallback={null}>
        <SignupModalContainer />
      </Suspense>
      <Suspense fallback={null}>
        <LoginModalContainer />
      </Suspense>
      <Suspense fallback={null}>
        <PhoneVerificationGate />
      </Suspense>

      {/* Wraps the page body too, so the homepage AI hero shares the header's on/off state. */}
      <AiSearchProvider>
        <div className="font-sans" id="app">
          {hasStuckHeaderShadow && <div ref={stuckHeaderCheckRef} />}

          <div
            id="global-headers"
            ref={headerRef}
            className={clsx(
              'relative',
              !nonStickyHeader &&
                !shouldHideHeaderAndFooter && {
                  'sticky top-0 z-1299': alwaysStickyHeaders,
                  'md:sticky md:top-0 md:z-1299': !alwaysStickyHeaders && !stickyFromLg,
                  'lg:sticky lg:top-0 lg:z-1299': !alwaysStickyHeaders && stickyFromLg,
                  'shadow-500': headerIsStuck && alwaysStickyHeaders,
                  'md:shadow-500': headerIsStuck && !alwaysStickyHeaders && !stickyFromLg,
                  'lg:shadow-500': headerIsStuck && !alwaysStickyHeaders && stickyFromLg,
                },
            )}>
            <Impersonated />
            <SystemDowntimeNotice />

            <div
              className={clsx({
                'hidden md:block': mobileHideHeaderAndFooter,
              })}>
              {/* Airbnb-style enable-bar; renders only for the AI-search experiment. */}
              <AiSearchTopBar />
              {!shouldHideHeaderAndFooter && (
                <GlobalHeader
                  isHomePage={pageProps.isHomePage}
                  isRvDeliveryPage={pageProps.isRvDeliveryPage}
                  isNewPromoPage={pageProps.isNewPromoPage}
                  isSERP={isSERP}
                  noSearchNavbar={noSearchNavbar}
                  isFluidPage={isFluidPage}
                  isTransparent={headerIsTransparent || headerDesktopTransparent}
                  isSticky={isMobile ? alwaysStickyHeaders : !nonStickyHeader}
                  isHeaderWhite={isHeaderWhite}
                  isDarkNavigation={isDarkNavigation}
                  isRVListingPage={isRVListingPage}
                  deferSearchNavigationUntilSubmit={deferSearchNavigationUntilSubmit}
                  isCampgroundSearchPage={isCampgroundSearchPage}
                />
              )}

              {pageProps.isNewPromoPage && (
                <PromoBanner
                  bannerText={pageProps.banner.text}
                  bannerIcon={pageProps.banner.icon}
                />
              )}
            </div>
          </div>

          <div
            style={{ backgroundColor: 'var(--page-content-bg-color)' }}
            className={clsx('relative min-h-[60vh]', {
              'transition-[background-color] duration-700 ease-in-out': isNewLayout,
            })}>
            {children}
          </div>

          <div
            className={clsx({
              'hidden md:block': mobileHideHeaderAndFooter,
            })}>
            {!shouldHideFooter && <Footer isNewLayout={isNewLayout} />}
          </div>
        </div>
      </AiSearchProvider>
    </>
  );
};

export default ApplicationLayout;
